<template>
  <div class="deal-task-config">
    <TextInput
      id="deal-task-title"
      label="Titel"
      :model-value="localConfig.title"
      placeholder="bijv. Bel de klant"
      @update:model-value="onField('title', $event)"
    />

    <div class="deal-task-config__vars-info">
      <BaseIcon name="info" size="13" class="deal-task-config__vars-icon" />
      <span>
        Gebruik <code>&#123;&#123;variabele&#125;&#125;</code> in titel of omschrijving
        voor dynamische waarden (bijv. <code>&#123;&#123;deal.title&#125;&#125;</code>).
      </span>
    </div>

    <details class="deal-task-config__vars">
      <summary>Beschikbare variabelen</summary>
      <div class="deal-task-config__vars-grid">
        <div
          v-for="group in templateVariables"
          :key="group.group"
          class="deal-task-config__vars-group"
        >
          <h4>{{ group.group }}</h4>
          <div
            v-for="v in group.vars"
            :key="v.key"
            class="deal-task-config__vars-item"
          >
            <code>&#123;&#123;{{ v.key }}&#125;&#125;</code>
            <span class="deal-task-config__vars-example">{{ v.example }}</span>
          </div>
        </div>
      </div>
      <p class="deal-task-config__vars-note">
        <code>proposal.*</code> is niet beschikbaar bij de
        <code>deal_created</code>-trigger.
      </p>
    </details>

    <TextAreaInput
      id="deal-task-description"
      label="Omschrijving (optioneel)"
      :model-value="localConfig.description"
      :rows="3"
      placeholder="Extra context voor de taak..."
      @update:model-value="onField('description', $event)"
    />

    <div class="deal-task-config__row">
      <SelectInput
        id="deal-task-type"
        label="Type"
        :model-value="localConfig.taskType"
        :options="taskTypeOptions"
        @update:model-value="onField('taskType', $event)"
      />
      <SelectInput
        id="deal-task-priority"
        label="Prioriteit"
        :model-value="localConfig.priority"
        :options="priorityOptions"
        @update:model-value="onField('priority', $event)"
      />
    </div>

    <NumberInput
      id="deal-task-due-in-days"
      label="Vervaldatum (dagen na uitvoering)"
      :model-value="localConfig.dueInDays"
      :min="0"
      :max="365"
      @update:model-value="onDueInDaysChange"
    />
    <p class="deal-task-config__hint">
      0 = vandaag, 1 = morgen, enz. De vervaldatum wordt berekend op het moment
      dat de automation deze stap uitvoert.
    </p>

    <SelectInput
      id="deal-task-assignee"
      label="Toewijzen aan"
      :model-value="localConfig.assignTo"
      :options="assigneeOptions"
      @update:model-value="onField('assignTo', $event)"
    />
    <p class="deal-task-config__hint">
      Standaard: deal-eigenaar. Bij een werkbon- of projecttrigger landt de taak op
      het project; kies dan eventueel projectmanager of monteur/inmeter.
    </p>
  </div>
</template>

<script>
  import { ref, computed, watch, onMounted } from 'vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import OrganizationService from '@/services/organizationService';
  import { useUserStore } from '@/store/userStore';
  import {
    DEAL_TASK_TYPES,
    TASK_PRIORITIES,
    TEMPLATE_VARIABLES,
  } from '@/constants/automations';

  const organizationService = new OrganizationService();

  function normalizeConfig(raw) {
    const validType = DEAL_TASK_TYPES.some((t) => t.value === raw?.taskType);
    const validPriority = TASK_PRIORITIES.some((p) => p.value === raw?.priority);
    const parsedDays = parseInt(raw?.dueInDays, 10);
    return {
      title: typeof raw?.title === 'string' ? raw.title : '',
      description: typeof raw?.description === 'string' ? raw.description : '',
      taskType: validType ? raw.taskType : 'general',
      priority: validPriority ? raw.priority : 'medium',
      dueInDays: Number.isInteger(parsedDays) && parsedDays >= 0 ? parsedDays : 0,
      assignTo: raw?.assignTo || 'deal_owner',
    };
  }

  export default {
    name: 'DealTaskConfig',

    components: {
      TextInput,
      TextAreaInput,
      SelectInput,
      NumberInput,
      BaseIcon,
    },

    props: {
      modelValue: {
        type: Object,
        required: true,
      },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      const userStore = useUserStore();
      const localConfig = ref(normalizeConfig(props.modelValue));
      const members = ref([]);

      const taskTypeOptions = DEAL_TASK_TYPES;
      const priorityOptions = TASK_PRIORITIES;
      const templateVariables = TEMPLATE_VARIABLES;

      const memberOptions = computed(() => {
        if (!Array.isArray(members.value)) return [];
        return members.value.map((member) => {
          const user = member.user || member;
          const id = user?.id ?? member.user_id;
          const label =
            [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
            user?.email ||
            member.email ||
            id;
          return { value: id, label };
        });
      });

      const assigneeOptions = computed(() => [
        { value: 'deal_owner', label: 'Deal-eigenaar' },
        { value: 'project_manager', label: 'Projectmanager' },
        { value: 'work_order_assignee', label: 'Monteur / inmeter van de werkbon' },
        ...memberOptions.value,
      ]);

      const emitUpdate = () => {
        emit('update:modelValue', { ...localConfig.value });
      };

      const onField = (field, value) => {
        localConfig.value[field] = value;
        emitUpdate();
      };

      const onDueInDaysChange = (value) => {
        const parsed = parseInt(value, 10);
        localConfig.value.dueInDays = Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
        emitUpdate();
      };

      const fetchMembers = async () => {
        const organizationId = userStore.currentOrganization?.id;
        if (!organizationId) return;
        try {
          members.value = await organizationService.getMembers(organizationId);
        } catch (error) {
          console.error('Failed to fetch members:', error);
          members.value = [];
        }
      };

      onMounted(fetchMembers);

      watch(
        () => props.modelValue,
        (newVal) => {
          const next = normalizeConfig(newVal);
          if (JSON.stringify(next) !== JSON.stringify(localConfig.value)) {
            localConfig.value = next;
          }
        },
        { deep: true }
      );

      return {
        localConfig,
        taskTypeOptions,
        priorityOptions,
        assigneeOptions,
        templateVariables,
        onField,
        onDueInDaysChange,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-task-config {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
    }

    &__hint {
      margin: 0;
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.4;
    }

    &__vars-info {
      display: flex;
      align-items: flex-start;
      gap: 0.4rem;
      padding: 0.5rem 0.6rem;
      background: var(--brand-50);
      border: 1px solid var(--brand-200);
      border-radius: 6px;
      font-size: 0.75rem;
      color: var(--brand-700);
      line-height: 1.4;

      code {
        font-family: monospace;
        background: var(--brand-100);
        padding: 0 3px;
        border-radius: 3px;
        font-size: 0.7rem;
      }
    }

    &__vars-icon {
      flex-shrink: 0;
      margin-top: 1px;
      color: var(--brand-500);
    }

    &__vars {
      border: 1px solid var(--slate-200);
      border-radius: 6px;
      padding: 0.6rem 0.75rem;

      summary {
        cursor: pointer;
        font-size: 0.8125rem;
        font-weight: 500;
        color: var(--slate-700);
        user-select: none;
      }
    }

    &__vars-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      gap: 1rem;
      margin-top: 0.75rem;
    }

    &__vars-group {
      h4 {
        font-size: 0.65rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--slate-400);
        margin: 0 0 0.4rem 0;
      }
    }

    &__vars-item {
      display: flex;
      flex-direction: column;
      gap: 1px;
      margin-bottom: 0.4rem;

      code {
        font-family: monospace;
        font-size: 0.7rem;
        color: var(--brand-700);
        background: var(--brand-50);
        padding: 1px 4px;
        border-radius: 3px;
        align-self: flex-start;
      }
    }

    &__vars-example {
      font-size: 0.65rem;
      color: var(--slate-400);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__vars-note {
      margin: 0.75rem 0 0 0;
      font-size: 0.7rem;
      color: var(--slate-500);
      line-height: 1.4;

      code {
        font-family: monospace;
        background: var(--slate-100);
        padding: 0 3px;
        border-radius: 3px;
        font-size: 0.65rem;
      }
    }
  }
</style>
